<template>
  <v-container>
    <!-- Empieza boton para exportar a excel -->
    <v-btn small class="ma-2" elevation="2" color="primary" @click="generateExcel">
      {{ excelButtonText }}
      <v-icon right> {{ excelIcon }} </v-icon>
    </v-btn>
    <!-- Termina boton para exportar a excel -->

    <!-- Empieza boton para exporta a pdf -->
    <v-btn small class="ma-2" elevation="2" color="primary" @click="generatePDF">
      {{ pdfButtonText }}
      <span aria-hidden="true">
        <v-icon right>{{ pdfIcon }}</v-icon>
      </span>
    </v-btn>
    <!-- Termina boton para exportar a pdf -->
  </v-container>
</template>

<script>
// Se importan las dependencias
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import writeExcelFile from 'write-excel-file/browser'

export default {
  name: 'DataExporter',

  // Propiedades del componente
  props: {
    dataArray: {
      type: Array,
      default: null,
    },

    dataFields: {
      type: Object,
      default: null,
    },

    fileName: {
      type: String,
      default: 'data',
    },

    formatRules: {
      type: Object,
      default: () => ({}),
    },

    footer: {
      type: String,
      default: 'TQO Transportation',
    },

    excelIcon: {
      type: String,
      default: 'mdi-file-excel-box',
    },

    pdfIcon: {
      type: String,
      default: 'mdi-file-pdf-box',
    },

    excelButtonText: {
      type: String,
      default: 'Descargar Excel',
    },

    pdfButtonText: {
      type: String,
      default: 'Descargar PDF',
    },
    subTitle: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: null,
    },
  },

  methods: {
    /**
     * Genera y descarga un archivo .xlsx con los datos proporcionados.
     *
     * Se genera un libro de Excel real con write-excel-file. vue-json-excel
     * guardaba una tabla HTML con extensión .xls, y Excel avisaba al abrirla de
     * que el formato no coincidía con la extensión (TQO-308)
     */
    async generateExcel() {
      if (!this.dataArray || !this.dataArray.length) return

      const columns = Object.entries(this.dataFields).map(([title, field]) => ({
        header: { value: title, fontWeight: 'bold' },
        cell: (item) => this.getExcelCell(item, title, field),
      }))

      try {
        await writeExcelFile(this.dataArray, { columns }).toFile(this.fileName + '.xlsx')
      } catch (error) {
        this.$store.commit('ALERT', {
          color: 'error',
          text: 'No se pudo generar el archivo de Excel',
        })
      }
    },

    /**
     * Construye una celda del Excel aplicando la regla de formato de su columna.
     * @param {Object} item - El registro de la fila.
     * @param {String} title - El título de la columna.
     * @param {String} field - La ruta de la propiedad en el registro.
     * @return {Object} La celda, con su valor y, si aplica, su formato numérico.
     */
    getExcelCell(item, title, field) {
      const value = this.getPropertyValue(item, field.split('.'))
      if (value === null) return { value: null }

      const rule = this.formatRules[title] || {}

      // Con excelFormat se escribe el número y el formato lo aplica Excel. La regla
      // format del PDF devuelve texto, y una columna de texto no se suma (TQO-308)
      const isNumeric = value !== '' && typeof value !== 'boolean' && Number.isFinite(Number(value))
      if (rule.excelFormat && isNumeric) {
        return { value: Number(value), format: rule.excelFormat }
      }

      const formatted = typeof rule.format === 'function' ? rule.format(value) : value

      return {
        value: ['string', 'number', 'boolean'].includes(typeof formatted)
          ? formatted
          : String(formatted),
      }
    },

    /**
     * Genera y descarga un PDF con los datos proporcionados.
     */
    generatePDF() {
      const doc = new jsPDF('landscape')
      const columnStyles = {}
      const tituloY = 10
      const subtituloY = 15
      const espacioExtra = 5
      const startY = subtituloY + espacioExtra
      // Configuración de estilos para el encabezado y las celdas
      const styles = {
        // Estilos para las celdas del encabezado
        headStyles: {
          fillColor: false, // Color de fondo del encabezado (azul)
          textColor: 0, // Color del texto (blanco)
          fontSize: 8, // Tamaño de la fuente
          fontStyle: 'bold', // Texto en negrita
          lineWidth: 0.1, // Grosor de la línea
        },
        // Estilos para las celdas del cuerpo
        bodyStyles: {
          fillColor: [220, 220, 220], // Color de fondo para las filas (gris claro)
          textColor: 0, // Color del texto (negro)
          fontSize: 6, // Tamaño de la fuente
          fontStyle: 'light',
        },
        // Alternar el color de fondo para las filas
        alternateRowStyles: {
          fillColor: 255, // Color de fondo para las filas alternas (blanco)
        },
      }

      Object.keys(this.dataFields).forEach((title, index) => {
        // Usando el título de la columna para encontrar la regla de formato
        const rule = this.formatRules[title]

        // Aplicar alineación solo si se especifica en formatRules
        if (rule && rule.align) {
          columnStyles[index] = { halign: rule.align }
        }
      })

      const body = this.getDataArrayForPDF()

      if (body.length) {
        doc.autoTable({
          startY: startY,
          head: [Object.keys(this.dataFields)],
          body,
          styles: styles.headStyles,
          bodyStyles: styles.bodyStyles,
          columnStyles: columnStyles,
          alternateRowStyles: styles.alternateRowStyles,
          // Nuevo
          didDrawPage: () => {
            // Añadir título y subtítulo
            doc.setFontSize(12)
            doc.text(this.title ? this.title : this.fileName, 14, tituloY)
            doc.setFontSize(9)
            doc.text(this.subTitle, 14, subtituloY)
          },
          // Termina nuevo
          didParseCell: (data) => {
            if (data.row.section === 'body') {
              const fieldKey = Object.keys(this.dataFields)[data.column.index]

              const rule = this.formatRules[fieldKey]
              if (rule && typeof rule.format === 'function') {
                data.cell.text = rule.format(data.cell.raw)
              }
            }
          },
        })

        // Añadir pie de página y numeración después de crear la tabla
        const pageCount = doc.internal.getNumberOfPages()

        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i)
          doc.setFontSize(9)
          doc.text(14, doc.internal.pageSize.height - 10, this.footer)
          doc.text(
            `${i} de ${pageCount}`,
            doc.internal.pageSize.width - 30,
            doc.internal.pageSize.height - 10
          )
        }
        doc.save(this.fileName + '.pdf')
      }
    },

    /**
     * Obtiene el valor de una propiedad de un objeto dada una ruta de acceso.
     * @param {Object} object - El objeto del cual obtener la propiedad.
     * @param {Array<String>} path - Ruta de acceso a la propiedad en el objeto.
     * @return {any} El valor de la propiedad si existe, de lo contrario null.
     */
    getPropertyValue(object, path) {
      // Se compara con null y no por valor falso: un 0 o un false son valores
      // válidos y se exportaban como celda vacía (TQO-308)
      const value = path.reduce(
        (acc, key) => (acc === null || acc === undefined ? acc : acc[key]),
        object
      )
      return value === undefined ? null : value
    },

    /**
     * Transforma el array de datos en un formato adecuado para el PDF.
     * @return {Array<Array<any>>} Un array de arrays con los datos para cada fila del PDF.
     */
    getDataArrayForPDF() {
      const paths = Object.values(this.dataFields).map((field) => field.split('.'))
      return this.dataArray.map((item) => paths.map((path) => this.getPropertyValue(item, path)))
    },
  },
}
</script>
